/**
 * Browser-local persistence for PositionTracker's four filter maps: positions
 * sport, positions market-kind, orders sport, orders market-kind. Keeps them
 * across reload and route-away/route-back (the maps otherwise live only in
 * component-local state and reset on remount — issue #83).
 *
 * All four ride in ONE object under ONE key. Each map serializes as an array of
 * [key, mode] entries (Map has no JSON form) and is rebuilt on read. Restore is
 * defensive per the issue: unknown/obsolete/malformed ENTRIES are dropped
 * individually (a since-removed sport doesn't wipe your other valid filters),
 * and a corrupt or wrong-shaped blob falls back to all-empty (= no filter).
 *
 * The key suffix versions the MEANING of the stored maps; enum drift needs no
 * version — it's absorbed by validation. `_v2` (#980) split the old
 * catch-all Moneyline kind into Moneyline / Props / Other. A v1 `moneyline`
 * entry therefore meant "all three", and is migrated to exactly that on
 * first read rather than silently narrowing a saved view; the migrated
 * scope raises a one-time notice so the trader sees why three chips lit up.
 * The v1 blob is left in place so an older build still reads its own data.
 */
import { SPORT_TABS, type Sport } from '@/views/ValueDashboard/types';
import type { SportFilter, SportFilterMode } from '@/lib/kalshiTicker/sportFilter';
import { storageKey } from '@/lib/infra/storage';
import {
  TRACKER_FAMILY_CHIPS,
  type TrackerFamilyFilter,
  type TrackerMarketFamily,
} from './trackerMarketFamily';

/** Exported so tests can seed raw/corrupt values under the exact key. */
export const STORAGE_KEY = storageKey('g1mt_tracker_filters_v2');
/** Pre-#980 key, read only to migrate. Exported for tests. */
export const LEGACY_V1_STORAGE_KEY = storageKey('g1mt_tracker_filters_v1');

export interface TrackerFilters {
  posSport: SportFilter;
  posKind: TrackerFamilyFilter;
  ordSport: SportFilter;
  ordKind: TrackerFamilyFilter;
  /** Per scope: the v1→v2 Moneyline split changed this scope's kind filter
   *  and the trader hasn't dismissed or reset the notice yet. */
  migrationNotice: { positions: boolean; orders: boolean };
}

const isValidSport = (k: string): k is Sport => SPORT_TABS.some((t) => t.id === k);
const isValidKind = (k: string): k is TrackerMarketFamily => TRACKER_FAMILY_CHIPS.some((c) => c.id === k);
/** v1 kinds: the three chips before the split. */
const isValidV1Kind = (k: string): k is 'moneyline' | 'spread' | 'total' =>
  k === 'moneyline' || k === 'spread' || k === 'total';
const isValidMode = (m: unknown): m is SportFilterMode => m === 'include' || m === 'exclude';

function emptyFilters(): TrackerFilters {
  return {
    posSport: new Map(),
    posKind: new Map(),
    ordSport: new Map(),
    ordKind: new Map(),
    migrationNotice: { positions: false, orders: false },
  };
}

/**
 * Rebuild one filter map from a stored value, dropping any entry that isn't a
 * [validKey, validMode] pair. A non-array (missing/garbage field) yields an
 * empty map without disturbing the other three.
 */
function reviveMap<K extends string>(
  raw: unknown,
  isValidKey: (k: string) => k is K,
): ReadonlyMap<K, SportFilterMode> {
  const out = new Map<K, SportFilterMode>();
  if (!Array.isArray(raw)) return out;
  for (const entry of raw) {
    if (!Array.isArray(entry) || entry.length !== 2) continue;
    const [key, mode] = entry;
    if (typeof key === 'string' && isValidKey(key) && isValidMode(mode)) {
      out.set(key, mode); // duplicate keys collapse, last-wins
    }
  }
  return out;
}

function serializeMap<K extends string>(
  map: ReadonlyMap<K, SportFilterMode>,
): Array<[K, SportFilterMode]> {
  return Array.from(map.entries());
}

/** v1 Moneyline was the catch-all for everything not a spread or total. */
const V1_MONEYLINE_FAMILIES: readonly TrackerMarketFamily[] = ['moneyline', 'props', 'other'];

function migrateV1Kind(raw: unknown): { kind: TrackerFamilyFilter; changed: boolean } {
  const kind = new Map<TrackerMarketFamily, SportFilterMode>();
  let changed = false;
  for (const [key, mode] of reviveMap(raw, isValidV1Kind)) {
    if (key === 'moneyline') {
      for (const family of V1_MONEYLINE_FAMILIES) kind.set(family, mode);
      changed = true;
    } else {
      kind.set(key, mode);
    }
  }
  return { kind, changed };
}

function parseObject(raw: string): Record<string, unknown> | null {
  const parsed: unknown = JSON.parse(raw);
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
    ? (parsed as Record<string, unknown>)
    : null;
}

function readV1(): TrackerFilters {
  const raw = localStorage.getItem(LEGACY_V1_STORAGE_KEY);
  const o = raw ? parseObject(raw) : null;
  if (!o) return emptyFilters();
  const pos = migrateV1Kind(o.posKind);
  const ord = migrateV1Kind(o.ordKind);
  return {
    posSport: reviveMap(o.posSport, isValidSport),
    posKind: pos.kind,
    ordSport: reviveMap(o.ordSport, isValidSport),
    ordKind: ord.kind,
    migrationNotice: { positions: pos.changed, orders: ord.changed },
  };
}

/** Read + validate the four maps, migrating a v1 blob when no v2 exists.
 *  Never throws; corrupt storage → no filter. A corrupt v2 does NOT fall back
 *  to v1 — that would resurrect a view the trader has since changed. */
export function readTrackerFilters(): TrackerFilters {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw == null) return readV1();
    const o = parseObject(raw);
    if (!o) return emptyFilters();
    const notice = o.migrationNotice as Record<string, unknown> | null | undefined;
    return {
      posSport: reviveMap(o.posSport, isValidSport),
      posKind: reviveMap(o.posKind, isValidKind),
      ordSport: reviveMap(o.ordSport, isValidSport),
      ordKind: reviveMap(o.ordKind, isValidKind),
      migrationNotice: {
        positions: notice?.positions === true,
        orders: notice?.orders === true,
      },
    };
  } catch {
    return emptyFilters();
  }
}

/** Persist all four maps. Never throws; a quota/availability failure is a no-op. */
export function writeTrackerFilters(filters: TrackerFilters): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        posSport: serializeMap(filters.posSport),
        posKind: serializeMap(filters.posKind),
        ordSport: serializeMap(filters.ordSport),
        ordKind: serializeMap(filters.ordKind),
        migrationNotice: filters.migrationNotice,
      }),
    );
  } catch {
    // localStorage unavailable or over quota — dropping the write is acceptable
    // for a display-only preference.
  }
}
